import {
  getBankStatementItemsTable,
  removeBankStatementItems,
  removeBankStatementItem,
} from "@/requests/finances/requestsBankOperations";

/*eslint  @typescript-eslint/no-use-before-define: 0*/
import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { FILTER_NAMES } from "@/static/filterNames";
import { DataOptions } from "vuetify";
import CriterionItemRequest from "@/models/api/filters/criterions/CriterionItemRequest";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { GetBankStatementItemsItem } from "@/models/api/finances/GetBankStatementItemsResponse";
import { BANK_STATEMENT_TYPES } from "@/static/bankStatementTypes";
import { PROMPT_TYPE, PromptModel } from "@/models/components/popup/Prompt";
import i18n from "@/plugins/i18n";
import { filterCapitalize } from "@/filters/capitalize";
import { SnackbarModel } from "@/models/components/popup/Snackbar";
import requiredValidator from "@/rules/requiredValidator";
import { getIsLockedBankStatementItemEdit } from "@/requests/finances/requestsBankTransfers";
import { AlertModelInterface } from "@/models/components/popup/Alert";
import { MESSAGE_TYPES } from "@/static/messageTypes";

/* eslint-disable-next-line */
export interface bankOperationsSaleLeftPanelStateInterface {
  isLoading: boolean;
  dataTableData: Array<GetBankStatementItemsItem>;
  dataTableOptions: DataOptions;
  countServerSide: number;
  filters: null | Array<CriterionItemRequest>;
  selectedItemId: null | number;
}

export const bankOperationsSaleLeftPanelState: bankOperationsSaleLeftPanelStateInterface = {
  isLoading: false,
  dataTableData: [],
  dataTableOptions: new DefaultDataOptions(),
  countServerSide: -1,
  filters: null,
  selectedItemId: null,
};

const bankOperationsSaleLeftPanelModule = defineModule({
  state: bankOperationsSaleLeftPanelState as bankOperationsSaleLeftPanelStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setIsLoading(state, param: boolean) {
      state.isLoading = param;
    },
    setDataTableData(state, param: Array<GetBankStatementItemsItem>) {
      state.dataTableData = param;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = JSON.parse(JSON.stringify(param));
    },
    setCountServerSide(state, param: number) {
      state.countServerSide = param;
    },
    setFilters(state, param: Array<CriterionItemRequest>) {
      state.filters = param;
    },
    setSelectedItemId(state, param: null | number) {
      state.selectedItemId = param;
    },
  },
  actions: {
    async getDataTableData(context) {
      const { commit, state, rootDispatch } = _moduleActionContext(context);
      commit.setIsLoading(true);
      const response = await getBankStatementItemsTable(
        {
          count: state.dataTableOptions.itemsPerPage,
          page: state.dataTableOptions.page - 1,
          criterions: state.filters,
          filterName: FILTER_NAMES.BankStatementItemStandardFilter,
        },
        BANK_STATEMENT_TYPES.Sale
      );
      commit.setCountServerSide(response.totalCount);
      commit.setDataTableData(response.items);
      commit.setIsLoading(false);
      if (response.items.length > 0) {
        commit.setSelectedItemId(response.items[0].id);
        rootDispatch.finances.bankOperations.saleRightPanel.getDataTableData(response.items[0].id);
      } else {
        commit.setSelectedItemId(null);
      }
    },
    async dropPayments(context, bankStatementItemIds: Array<number>) {
      const { rootDispatch, dispatch } = _moduleActionContext(context);
      rootDispatch.failIfRequestsInProgress();
      if (bankStatementItemIds.length < 1) {
        rootDispatch.popup.addSnackbar(new SnackbarModel(filterCapitalize(i18n.t("noBankStatementSelected")), "error"));
      }
      let promptContent = ` ${filterCapitalize(i18n.t("selectedBankStatements"))}: ${
        bankStatementItemIds.length
      } <br/>`;
      promptContent += filterCapitalize(i18n.t("addCommentAboutReasonOfDroppingPayment")) + ":";
      const promptTitle = filterCapitalize(i18n.t("paymentDropping"));
      const promptModel = new PromptModel(promptContent, promptTitle, PROMPT_TYPE.TEXTAREA);
      promptModel.placeholder = filterCapitalize(i18n.t("comment"));
      promptModel.width = "500px";
      promptModel.rules = [requiredValidator];
      const promptResponse = (await rootDispatch.popup.addPrompt(promptModel)) as string;
      await removeBankStatementItems(bankStatementItemIds, promptResponse);
      rootDispatch.popup.addOperationSuccessfulSnackbar();
      dispatch.getDataTableData();
    },

    async dropPayment(context) {
      const { state, rootDispatch, dispatch } = _moduleActionContext(context);
      if (state.selectedItemId !== null) {
        rootDispatch.failIfRequestsInProgress();

        const selectedItem = state.dataTableData.find((x) => x.id == state.selectedItemId);
        if (selectedItem && selectedItem.amountLeft == 0) {
          const removeBSItem = await dispatch.canUserRemoveBankStatementItem([state.selectedItemId]);
          if (removeBSItem) {
            await removeBankStatementItem(state.selectedItemId, "");
            rootDispatch.popup.addOperationSuccessfulSnackbar();
            dispatch.getDataTableData();
          }
        } else {
          const dropBSItem = await dispatch.canUserEditLockedBankStatementItem({
            bankStatementItemId: state.selectedItemId,
            messageQuestion: "areYouSureYouWantDropPayment?",
          });
          if (dropBSItem) {
            const promptContent = filterCapitalize(i18n.t("addCommentAboutReasonOfDroppingPayment")) + ":";
            const promptTitle = filterCapitalize(i18n.t("paymentDropping"));
            const promptModel = new PromptModel(promptContent, promptTitle, PROMPT_TYPE.TEXTAREA);
            promptModel.width = "500px";
            promptModel.rules = [requiredValidator];
            promptModel.placeholder = filterCapitalize(i18n.t("comment"));
            const promptResponse = (await rootDispatch.popup.addPrompt(promptModel)) as string;
            await removeBankStatementItem(state.selectedItemId, promptResponse);
            rootDispatch.popup.addOperationSuccessfulSnackbar();
            dispatch.getDataTableData();
          }
        }
      }
    },
    async canUserRemoveBankStatementItem(context, bankStatementIds: Array<number>) {
      const { rootDispatch } = _moduleActionContext(context);
      const isLockedEditResponse = await getIsLockedBankStatementItemEdit(bankStatementIds);
      if (!isLockedEditResponse.isLockedEdit) {
        if (await rootDispatch.popup.addConfirmRemoveElement()) return true;
        else return false;
      }
      if (isLockedEditResponse.isLockedEdit && isLockedEditResponse.userHasPermission) {
        if (
          await rootDispatch.popup.addConfirmRemoveElementWithCustomMessage(
            i18n.t("editThisItemIsLockedButYouHavePermissionToChange") +
              " " +
              filterCapitalize(i18n.t("doYouReallyWantToRemoveElement?"))
          )
        )
          return true;
        else return false;
      }
      rootDispatch.popup.addAlert({
        title: filterCapitalize(i18n.t("error")),
        content: filterCapitalize(i18n.t("noPermissions")),
        type: MESSAGE_TYPES.Error,
      } as AlertModelInterface);
      return false;
    },
    async canUserEditLockedBankStatementItem(context, value: { bankStatementItemId: number; messageQuestion: string }) {
      const { rootDispatch } = _moduleActionContext(context);
      const isLockedEditResponse = await getIsLockedBankStatementItemEdit([value.bankStatementItemId]);
      if (!isLockedEditResponse.isLockedEdit) return true;
      if (isLockedEditResponse.isLockedEdit && isLockedEditResponse.userHasPermission) {
        if (
          await rootDispatch.popup.addConfirmRemoveElementWithCustomMessage(
            i18n.t("editThisItemIsLockedButYouHavePermissionToChange") +
              " " +
              filterCapitalize(i18n.t(value.messageQuestion))
          )
        )
          return true;
        else return false;
      }
      rootDispatch.popup.addAlert({
        title: filterCapitalize(i18n.t("error")),
        content: filterCapitalize(i18n.t("noPermissions")),
        type: MESSAGE_TYPES.Error,
      } as AlertModelInterface);
      return false;
    },
  },
});

export default bankOperationsSaleLeftPanelModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, bankOperationsSaleLeftPanelModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, bankOperationsSaleLeftPanelModule);
